import React, { useEffect, useRef, useState } from 'react';

const Blogs = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [activeFilter, setActiveFilter] = useState('All');
    const [filteredBlogs, setFilteredBlogs] = useState([]);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    const blogs = [
        {
            title: 'The Future of AI in Enterprise Software',
            category: 'Technology',
            excerpt: 'Exploring how Artificial Intelligence is revolutionizing business operations and decision-making processes in modern enterprises.',
            date: 'March 15, 2025',
            readTime: '5 min read',
            author: 'Sarah Johnson',
            image: '/api/placeholder/600/400',
            tags: ['AI', 'Enterprise', 'Innovation'],
            color: 'from-blue-600 to-purple-600'
        },
        {
            title: 'Cybersecurity Best Practices for 2025',
            category: 'Security',
            excerpt: 'Essential security measures every organization needs to implement to protect their digital assets in an evolving threat landscape.',
            date: 'March 10, 2025',
            readTime: '7 min read',
            author: 'Michael Chen',
            image: '/api/placeholder/600/400',
            tags: ['Security', 'Cloud', 'Data Protection'],
            color: 'from-green-500 to-teal-500'
        },
        {
            title: 'Digital Transformation Success Stories',
            category: 'Business',
            excerpt: 'Case studies of successful digital transformations and the key strategies that led to improved operational efficiency.',
            date: 'March 5, 2025',
            readTime: '6 min read',
            author: 'Emily Davis',
            image: '/api/placeholder/600/400',
            tags: ['Digital Transformation', 'Strategy', 'Growth'],
            color: 'from-orange-500 to-red-500'
        },
        {
            title: 'Cloud Computing Trends to Watch',
            category: 'Cloud',
            excerpt: 'A deep dive into emerging cloud technologies, including serverless architecture and edge computing trends.',
            date: 'February 28, 2025',
            readTime: '4 min read',
            author: 'David Wilson',
            image: '/api/placeholder/600/400',
            tags: ['Cloud', 'AWS', 'Azure'],
            color: 'from-indigo-500 to-purple-500'
        },
        {
            title: 'Optimizing Supply Chain with Blockchain',
            category: 'Technology',
            excerpt: 'How blockchain technology is bringing transparency, security, and efficiency to global supply chain networks.',
            date: 'February 20, 2025',
            readTime: '8 min read',
            author: 'James Anderson',
            image: '/api/placeholder/600/400',
            tags: ['Blockchain', 'Supply Chain', 'Logistics'],
            color: 'from-cyan-500 to-blue-500'
        },
        {
            title: 'The Rise of EdTech in Modern Learning',
            category: 'Education',
            excerpt: 'Analyzing the impact of technology on education and how digital platforms are reshaping the learning experience.',
            date: 'February 15, 2025',
            readTime: '5 min read',
            author: 'Lisa Thompson',
            image: '/api/placeholder/600/400',
            tags: ['EdTech', 'E-Learning', 'Future of Work'],
            color: 'from-purple-500 to-pink-500'
        }
    ];

    const filters = ['All', 'Technology', 'Business', 'Security', 'Cloud', 'Education'];

    useEffect(() => {
        if (activeFilter === 'All') {
            setFilteredBlogs(blogs);
        } else {
            setFilteredBlogs(blogs.filter(blog => blog.category === activeFilter));
        }
    }, [activeFilter]);

    return (
        <section id="blogs" ref={sectionRef} className="section-padding bg-white">
            <div className="container-custom">
                {/* Section Header */}
                <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                        Latest <span className="gradient-text">Insights</span>
                    </h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mb-8"></div>
                    <p className="text-lg md:text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed text-left">
                        Stay updated with the latest trends, technologies, and strategies driving digital innovation.
                        Our experts share their knowledge to help you stay ahead.
                    </p>
                </div>

                {/* Filter Tabs */}
                <div className={`flex flex-wrap justify-center mb-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                    {filters.map((filter) => (
                        <button
                            key={filter}
                            onClick={() => setActiveFilter(filter)}
                            className={`mx-2 my-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeFilter === filter
                                ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-lg'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                        >
                            {filter}
                            <span className="ml-2 text-xs opacity-75">
                                ({filter === 'All' ? blogs.length : blogs.filter(b => b.category === filter).length})
                            </span>
                        </button>
                    ))}
                </div>

                {/* Blogs Grid */}
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
                    {filteredBlogs.map((blog, index) => (
                        <div
                            key={index}
                            className={`group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden card-hover flex flex-col h-full ${isVisible ? 'animate-fadeInUp' : 'opacity-0'
                                }`}
                            style={{ animationDelay: `${index * 0.1 + 0.4}s` }}
                        >
                            {/* Blog Image Header */}
                            <div className={`h-48 bg-gradient-to-br ${blog.color} relative overflow-hidden flex-shrink-0`}>
                                <div className="absolute inset-0 bg-black/10 transition-opacity group-hover:bg-black/20"></div>

                                <div className="absolute top-4 left-4">
                                    <span className="bg-white/90 backdrop-blur-sm text-slate-800 px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                                        {blog.category}
                                    </span>
                                </div>

                                {/* Decorative Elements */}
                                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full"></div>
                                <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-white/10 rounded-full"></div>
                            </div>

                            {/* Blog Content */}
                            <div className="p-6 flex flex-col flex-grow">
                                <div className="flex items-center text-xs text-slate-500 mb-3 space-x-4">
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        {blog.date}
                                    </span>
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {blog.readTime}
                                    </span>
                                </div>

                                <h3 className="text-xl font-bold text-slate-900 font-display mb-3 group-hover:text-blue-600 transition-colors line-clamp-2 text-left">
                                    {blog.title}
                                </h3>

                                <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3 flex-grow text-left">
                                    {blog.excerpt}
                                </p>

                                {/* Tags */}
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {blog.tags.slice(0, 3).map((tag, tagIndex) => (
                                        <span key={tagIndex} className="px-2 py-1 bg-slate-50 text-slate-500 rounded text-xs">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>

                                <div className="pt-4 mt-auto border-t border-slate-100 flex items-center justify-between">
                                    <span className="text-sm font-medium text-slate-700">
                                        By {blog.author}
                                    </span>
                                    <button className="text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors flex items-center group/btn">
                                        Read More
                                        <svg className="w-4 h-4 ml-1 transform transition-transform group-hover/btn:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Load More Button */}
                {filteredBlogs.length > 6 && (
                    <div className={`text-center mt-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
                        <button className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200">
                            Load More Articles
                        </button>
                    </div>
                )}

                {/* Newsletter CTA */}
                <div className={`mt-20 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '1s' }}>
                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 text-white text-center relative overflow-hidden">
                        {/* Decorative background */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl -ml-16 -mb-16"></div>

                        <div className="relative z-10">
                            <h3 className="font-display text-3xl md:text-4xl font-semibold mb-4">
                                Subscribe to Our Newsletter
                            </h3>
                            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                                Get the latest tech insights, industry news, and expert articles delivered directly to your inbox.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                                <input
                                    type="email"
                                    placeholder="Enter your email address"
                                    className="px-6 py-4 rounded-xl text-slate-900 bg-white/95 focus:outline-none focus:ring-4 focus:ring-blue-500/50 w-full"
                                />
                                <button className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200 whitespace-nowrap">
                                    Subscribe
                                </button>
                            </div>
                            <p className="text-gray-400 text-sm mt-4">We respect your privacy. Unsubscribe at any time.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Blogs;
